import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Upload, X, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { formatApiErrorDetail } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ image_url: '', title: '', caption: '', order: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchSlides = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/carousel`);
      setSlides(data);
    } catch (error) {
      console.error('Carousel fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image_url: data.url }));
    } catch (err) {
      setError('Image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      setError('Please choose a photo from your device OR paste an image URL above.');
      return;
    }
    setError('');
    try {
      const newOrder = slides.length;
      await axios.post(`${API_URL}/api/carousel`, { ...formData, order: newOrder }, { withCredentials: true });
      setSuccess('Slide added!');
      fetchSlides();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to add slide');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this slide from the carousel?')) return;
    try {
      await axios.delete(`${API_URL}/api/carousel/${id}`, { withCredentials: true });
      setSuccess('Slide removed!');
      fetchSlides();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Delete failed');
    }
  };

  const moveSlide = async (slide, direction) => {
    const currentIndex = slides.findIndex(s => s.id === slide.id);
    const newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= slides.length) return;

    const swapSlide = slides[newIndex];
    try {
      await Promise.all([
        axios.put(`${API_URL}/api/carousel/${slide.id}`, { image_url: slide.image_url, title: slide.title, caption: slide.caption, order: newIndex }, { withCredentials: true }),
        axios.put(`${API_URL}/api/carousel/${swapSlide.id}`, { image_url: swapSlide.image_url, title: swapSlide.title, caption: swapSlide.caption, order: currentIndex }, { withCredentials: true })
      ]);
      fetchSlides();
    } catch (error) {
      console.error('Reorder failed:', error);
    }
  };

  const resetForm = () => {
    setFormData({ image_url: '', title: '', caption: '', order: 0 });
    setShowModal(false);
    setError('');
  };

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  return (
    <div data-testid="admin-carousel-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-2" data-testid="carousel-title">Manage Carousel</h1>
          <p className="text-text_muted">Add, remove, and reorder homepage gallery slides</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all"
          data-testid="add-slide-button"
        >
          <Plus size={20} />
          <span>Add Slide</span>
        </button>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-700" data-testid="success-message">{success}</div>
      )}

      {loading ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        </div>
      ) : slides.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-sm border border-border">
          <p className="text-xl text-text_muted mb-4">No carousel slides yet.</p>
          <p className="text-text_muted">Click "Add Slide" to upload your first image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {slides.map((slide, index) => (
            <div key={slide.id} className="bg-white rounded-sm border border-border overflow-hidden group" data-testid={`carousel-slide-card-${index}`}>
              <div className="relative h-48">
                <img src={getImageSrc(slide.image_url)} alt={slide.title || `Slide ${index + 1}`} className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-bold px-2 py-1 rounded">
                  #{index + 1}
                </div>
              </div>
              <div className="p-4">
                {slide.title && <h3 className="font-semibold text-primary mb-1 truncate">{slide.title}</h3>}
                {slide.caption && <p className="text-sm text-text_muted truncate">{slide.caption}</p>}
                {!slide.title && !slide.caption && <p className="text-sm text-text_muted italic">No caption</p>}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => moveSlide(slide, -1)}
                      disabled={index === 0}
                      className="p-1.5 text-text_muted hover:bg-surface rounded disabled:opacity-30 transition-colors"
                      data-testid={`move-up-${index}`}
                      title="Move earlier"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      onClick={() => moveSlide(slide, 1)}
                      disabled={index === slides.length - 1}
                      className="p-1.5 text-text_muted hover:bg-surface rounded disabled:opacity-30 transition-colors"
                      data-testid={`move-down-${index}`}
                      title="Move later"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </div>
                  <button
                    onClick={() => handleDelete(slide.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    data-testid={`delete-slide-${index}`}
                    title="Remove slide"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Slide Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="carousel-modal">
          <div className="bg-white rounded-sm max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-2xl font-bold text-primary">Add Carousel Slide</h2>
              <button onClick={resetForm} className="text-text_muted hover:text-primary">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">{error}</div>
              )}

              <div className="space-y-4">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Image *</label>
                  {formData.image_url && (
                    <div className="relative w-full h-48 rounded overflow-hidden border border-border mb-3">
                      <img src={getImageSrc(formData.image_url)} alt="Preview" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full">
                        <X size={14} />
                      </button>
                    </div>
                  )}

                  {/* Direct file chooser - works on all devices */}
                  <div className="mb-3">
                    <label className="block text-xs text-text_muted mb-1 font-medium">Choose from your device:</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="w-full text-sm text-text_muted file:mr-4 file:py-2.5 file:px-5 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-opacity-90 file:cursor-pointer cursor-pointer border border-input rounded"
                      data-testid="upload-carousel-file-input"
                    />
                    {uploading && <p className="text-xs text-accent mt-1 font-medium">Uploading... please wait</p>}
                  </div>

                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs text-text_muted font-medium">OR</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>

                  <label className="block text-xs text-text_muted mb-1 font-medium">Paste image URL:</label>
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                    data-testid="input-carousel-image-url"
                  />
                </div>

                {/* Title (optional) */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Title (optional)</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., JVR Forgings 1 MW Rooftop"
                    className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    data-testid="input-carousel-title"
                  />
                </div>

                {/* Caption (optional) */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Caption (optional)</label>
                  <input
                    type="text"
                    value={formData.caption}
                    onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                    placeholder="e.g., Ludhiana, Punjab | 1 MW Rooftop Solar"
                    className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    data-testid="input-carousel-caption"
                  />
                </div>

                <div className="flex space-x-4 pt-2">
                  <button type="submit" className="flex-1 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all" data-testid="carousel-modal-submit">
                    Add Slide
                  </button>
                  <button type="button" onClick={resetForm} className="px-6 py-3 bg-surface text-text_main rounded font-semibold hover:bg-border transition-all">
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCarousel;
